// src/pages/settings/loads/LoadTemplatesTab.jsx

import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useDebouncedSearch } from '../../../hooks/useDebouncedSearch';
import SearchInput from '../../../components/SearchInput/SearchInput';
import { apiErrorMessage } from '../../../utils/errors';
import {
  useListLoadOptions,
  useCreateLoadOption,
  useUpdateLoadOption,
  useDeleteLoadOption,
  useListCustomerLocations,
} from '../../../features/loads/api';

// Form rules — pure, unit-tested, no React.
// See features/loads/loadTemplateForm.
import {
  EMPTY_TEMPLATE,
  buildTemplatePayload,
  readAssignedLocationIds,
  readTemplateForm,
  filterTemplates,
  flattenLocationPages,
  getCustomerLocationLabel,
  mergeEditLocations,
  templateSaveErrorMessage,
  toggleLocationId,
  validateTemplate,
} from '../../../features/loads/loadTemplateForm';

interface CustomerLocationPickerProps {
  search: string;
  onSearchChange: (value: string) => void;
  locations: any[];
  isLoading?: boolean;
  selectedIds: string[];
  onChange: (locationId: string, checked: boolean) => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore: () => void;
  emptyText?: string;
  variant?: string;
}

function CustomerLocationPicker({
  search,
  onSearchChange,
  locations,
  isLoading,
  selectedIds,
  onChange,

  hasMore = false,
  isLoadingMore = false,
  onLoadMore,

  emptyText = 'No customer locations found.',
}: CustomerLocationPickerProps) {
  return (
    <div>
      <SearchInput
        className="load-location-picker__search"
        value={search}
        onValueChange={onSearchChange}
        placeholder="Search customer or location..."
      />

      <div
        className="muted tiny"
        style={{
          marginTop: 4,
        }}
      >
        Search by customer name, location name, address, city, state, or ZIP code.
      </div>

      <div className="load-location-picker__list">
        {isLoading ? (
          <div className="muted tiny">Loading customer locations...</div>
        ) : locations.length === 0 ? (
          <div className="muted tiny">{emptyText}</div>
        ) : (
          <>
            {locations.map((location: any) => {
              const checked = selectedIds.includes(location.id);

              const { customerName, customerEmail, locationName, address } =
                getCustomerLocationLabel(location);

              return (
                <label
                  key={location.id}
                  className={`load-location-picker__option ${checked ? 'is-selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => {
                      onChange(location.id, event.target.checked);
                    }}
                    style={{
                      marginTop: 3,
                      flexShrink: 0,
                    }}
                  />

                  <span
                    style={{
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',

                        alignItems: 'baseline',

                        gap: 6,

                        flexWrap: 'wrap',
                      }}
                    >
                      <strong>{customerName}</strong>

                      {customerEmail && customerEmail !== customerName && (
                        <span className="muted tiny">{customerEmail}</span>
                      )}
                    </span>

                    <span
                      style={{
                        display: 'block',
                        marginTop: 2,
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    >
                      {locationName}

                      {location.isPrimary ? ' · Primary' : ''}
                    </span>

                    {address && (
                      <span
                        className="muted tiny"
                        style={{
                          display: 'block',
                          marginTop: 2,
                        }}
                      >
                        {address}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}

            {hasMore && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '10px 4px 4px',
                }}
              >
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={onLoadMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {selectedIds.length > 0 && (
        <div
          className="muted tiny"
          style={{
            marginTop: 6,
          }}
        >
          {selectedIds.length} customer location
          {selectedIds.length === 1 ? '' : 's'} selected
        </div>
      )}
    </div>
  );
}

function AssignedLocationsSummary({ locations = [] }: { locations?: any[] }) {
  if (!locations.length) {
    return <span className="muted">Not assigned</span>;
  }

  const visible = locations.slice(0, 2);
  const remaining = locations.length - visible.length;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 180,
      }}
    >
      {visible.map((location: any) => {
        const { customerName, locationName } = getCustomerLocationLabel(location);

        return (
          <div
            key={location.deliveryAddressId || location.id}
            title={[customerName, locationName].filter(Boolean).join(' · ')}
            style={{
              fontSize: 12,
              lineHeight: 1.3,
            }}
          >
            <strong>{customerName}</strong>
            {' · '}
            {locationName}

            {location.isDefault && (
              <span
                className="muted tiny"
                style={{
                  marginLeft: 4,
                }}
              >
                (default)
              </span>
            )}
          </div>
        );
      })}

      {remaining > 0 && <div className="muted tiny">+{remaining} more</div>}
    </div>
  );
}

export default function LoadTemplatesTab() {
  const { data: rows = [], isLoading } = useListLoadOptions();

  const createItem = useCreateLoadOption();

  const updateItem = useUpdateLoadOption();

  const deleteItem = useDeleteLoadOption();

  const [q, setQ] = useState('');

  const [draft, setDraft] = useState(EMPTY_TEMPLATE);

  const [creating, setCreating] = useState(false);

  const [editId, setEditId] = useState<string | null>(null);

  const [editForm, setEditForm] = useState(EMPTY_TEMPLATE);

  const [saving, setSaving] = useState(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  /*
   * Create-location picker.
   */
  const [locationSearch, setLocationSearch] = useState('');

  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);

  /*
   * Edit-location picker.
   */
  const [editLocationSearch, setEditLocationSearch] = useState('');

  const [editLocationIds, setEditLocationIds] = useState<string[]>([]);

  /*
   * Keep create and edit search calls
   * independent so editing a template
   * doesn't disturb the create form.
   */
  const debouncedLocationSearch = useDebouncedSearch(locationSearch, 300);

  const {
    data: createLocationsData,
    isLoading: createLocationsLoading,

    isFetchingNextPage: createLocationsFetchingMore,

    hasNextPage: createLocationsHasMore,

    fetchNextPage: fetchMoreCreateLocations,
  } = useListCustomerLocations(debouncedLocationSearch);

  const debouncedEditLocationSearch = useDebouncedSearch(editLocationSearch, 300);

  const {
    data: editLocationsData,
    isLoading: editLocationsLoading,

    isFetchingNextPage: editLocationsFetchingMore,

    hasNextPage: editLocationsHasMore,

    fetchNextPage: fetchMoreEditLocations,
  } = useListCustomerLocations(debouncedEditLocationSearch);
  const createLocations = useMemo(
    () => flattenLocationPages(createLocationsData),
    [createLocationsData]
  );

  const editSearchLocations = useMemo(
    () => flattenLocationPages(editLocationsData),
    [editLocationsData]
  );

  const editingRow = useMemo(
    () => rows.find((row: any) => row.id === editId) ?? null,
    [rows, editId]
  );

  /*
   * Assigned locations stay visible even when they don't match the current
   * edit search — see mergeEditLocations.
   */
  const editLocations = useMemo(
    () => mergeEditLocations(editingRow?.assignedLocations ?? [], editSearchLocations),
    [editingRow?.assignedLocations, editSearchLocations]
  );

  const filtered = useMemo(() => filterTemplates(rows, q), [rows, q]);

  const toggleCreateLocation = (locationId: string, checked: boolean) => {
    setSelectedLocationIds((previous) => toggleLocationId(previous, locationId, checked));
  };

  const toggleEditLocation = (locationId: string, checked: boolean) => {
    setEditLocationIds((previous) => toggleLocationId(previous, locationId, checked));
  };

  const onCreate = () => {
    const invalid = validateTemplate(draft);

    if (invalid) {
      toast.warn(invalid);

      return;
    }

    setCreating(true);

    createItem.mutate(buildTemplatePayload(draft, selectedLocationIds), {
      onSuccess: () => {
        toast.success('Template added.');

        setDraft(EMPTY_TEMPLATE);

        setSelectedLocationIds([]);

        setLocationSearch('');
      },

      onError: (error) => {
        const response = (error as any)?.response?.data;

        toast.error(templateSaveErrorMessage(response?.error, response?.message, 'Create failed.'));
      },

      onSettled: () => setCreating(false),
    });
  };

  const beginEdit = (row: any) => {
    setEditId(row.id);

    setEditForm(readTemplateForm(row));

    setEditLocationIds(readAssignedLocationIds(row));

    setEditLocationSearch('');
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditForm(EMPTY_TEMPLATE);
    setEditLocationIds([]);
    setEditLocationSearch('');
  };

  const onSave = () => {
    if (!editId) {
      return;
    }

    const invalid = validateTemplate(editForm);

    if (invalid) {
      toast.warn(invalid);

      return;
    }

    setSaving(true);

    updateItem.mutate(
      {
        id: editId,

        data: buildTemplatePayload(editForm, editLocationIds),
      },
      {
        onSuccess: () => {
          toast.success('Template updated.');

          cancelEdit();
        },

        onError: (error) => {
          const response = (error as any)?.response?.data;

          toast.error(templateSaveErrorMessage(response?.error, response?.message));
        },

        onSettled: () => setSaving(false),
      }
    );
  };

  const onDelete = (row: any) => {
    if (!window.confirm(`Delete "${row.name || '(no name)'}"?`)) {
      return;
    }

    setDeletingId(row.id);

    deleteItem.mutate(row.id, {
      onSuccess: () => {
        toast.success('Template deleted.');
      },

      onError: (error) => {
        toast.error(apiErrorMessage(error, 'Delete failed.'));
      },

      onSettled: () => setDeletingId(null),
    });
  };

  return (
    <div className="card card--bleed-top">
      {/* ─────────────────────────────
          CREATE
      ───────────────────────────── */}

      <div className="grid grid--2">
        <div className="form-control">
          <label>Name *</label>

          <input
            value={draft.name}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                name: event.target.value,
              }))
            }
            placeholder='e.g. "Truck", "20-ton"'
          />
        </div>

        <div className="form-control">
          <label>Capacity Label (tons)</label>

          <input
            type="number"
            value={draft.capacityLabel}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                capacityLabel: event.target.value,
              }))
            }
            placeholder='e.g. "20"'
          />
        </div>

        <div className="load-template-create__options">
          <label className="load-template-default-field__label">
            <input
              type="checkbox"
              checked={!!draft.enableCarrierRate}
              onChange={(e) =>
                setDraft((current) => ({
                  ...current,
                  enableCarrierRate: e.target.checked,
                }))
              }
            />
            <span>Enable carrier rate</span>
          </label>

          <div className="load-template-default-field">
            <label className="load-template-default-field__label">
              <input
                type="checkbox"
                checked={!!draft.isDefault}
                onChange={(e) =>
                  setDraft((current) => ({
                    ...current,
                    isDefault: e.target.checked,
                  }))
                }
              />
              <span>Default fulfilment method</span>
            </label>

            <div className="muted tiny load-template-default-field__hint">
              The default template will be selected automatically where applicable.
            </div>
          </div>
        </div>

        <div
          className="form-control"
          style={{
            gridColumn: '1 / -1',
          }}
        >
          <label>Assign to Customer Locations</label>

          <div
            className="muted tiny"
            style={{
              marginBottom: 8,
            }}
          >
            Optional. Select the delivery locations where this fulfilment method should be
            available.
          </div>

          <CustomerLocationPicker
            search={locationSearch}
            onSearchChange={setLocationSearch}
            locations={createLocations}
            isLoading={createLocationsLoading}
            selectedIds={selectedLocationIds}
            onChange={toggleCreateLocation}
            hasMore={!!createLocationsHasMore}
            isLoadingMore={createLocationsFetchingMore}
            onLoadMore={() => fetchMoreCreateLocations()}
          />
        </div>
      </div>

      <div className="actions">
        <button className="btn btn--primary" onClick={onCreate} disabled={creating}>
          {creating ? 'Adding...' : 'Add Template'}
        </button>
      </div>

      {/* ─────────────────────────────
          SEARCH
      ───────────────────────────── */}

      <div className="search-row">
        <SearchInput
          className="search-input"
          wrapperClassName="search-input-clearable--narrow"
          placeholder="Search templates or assigned locations..."
          value={q}
          onValueChange={setQ}
        />
      </div>

      {/* ─────────────────────────────
          TABLE
      ───────────────────────────── */}

      <div className="card card--bleed-top">
        {isLoading ? (
          <div className="skeleton h-40 w-full" />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>

                <th>Capacity Label (tons)</th>

                <th className="ta-center">Default</th>

                <th className="ta-center">Carrier Rate</th>

                <th>Assigned Locations</th>

                <th className="col-actions ta-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((row: any) => {
                const editing = editId === row.id;

                return (
                  <>
                    <tr
                      key={row.id}
                      className={
                        editing
                          ? 'load-template-row load-template-row--editing'
                          : 'load-template-row'
                      }
                    >
                      <td>
                        {editing ? (
                          <input
                            className="load-template-edit-input load-template-edit-input--number"
                            value={editForm.name}
                            onChange={(event) =>
                              setEditForm((current) => ({
                                ...current,

                                name: event.target.value,
                              }))
                            }
                          />
                        ) : (
                          row.name || <span className="muted">—</span>
                        )}
                      </td>

                      <td>
                        {editing ? (
                          <input
                            className="load-template-edit-input load-template-edit-input--number"
                            type="number"
                            value={editForm.capacityLabel}
                            onChange={(event) =>
                              setEditForm((current) => ({
                                ...current,

                                capacityLabel: event.target.value,
                              }))
                            }
                          />
                        ) : (
                          row.capacityLabel || <span className="muted">—</span>
                        )}
                      </td>

                      <td className="ta-center">
                        {editing ? (
                          <label
                            className="load-template-checkbox"
                            title="Set as default fulfilment method"
                          >
                            <input
                              type="checkbox"
                              checked={!!editForm.isDefault}
                              onChange={(event) =>
                                setEditForm((current) => ({
                                  ...current,
                                  isDefault: event.target.checked,
                                }))
                              }
                            />

                            <span className="load-template-checkbox__box" />
                          </label>
                        ) : row.isDefault ? (
                          <span className="badge">Default</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>

                      <td className="ta-center">
                        {editing ? (
                          <label className="load-template-checkbox">
                            <input
                              type="checkbox"
                              checked={!!editForm.enableCarrierRate}
                              onChange={(event) =>
                                setEditForm((current) => ({
                                  ...current,
                                  enableCarrierRate: event.target.checked,
                                }))
                              }
                            />

                            <span className="load-template-checkbox__box" />
                          </label>
                        ) : row.enableCarrierRate ? (
                          <span className="badge">Enabled</span>
                        ) : (
                          <span className="badge">Disabled</span>
                        )}
                      </td>

                      <td>
                        {editing ? (
                          <span className="muted tiny">{editLocationIds.length} selected</span>
                        ) : (
                          <AssignedLocationsSummary locations={row.assignedLocations ?? []} />
                        )}
                      </td>

                      <td className="col-actions actions-cell ta-right">
                        {editing ? (
                          <>
                            <button className="btn btn--primary" onClick={onSave} disabled={saving}>
                              {saving ? 'Saving...' : 'Save'}
                            </button>

                            <button
                              className="btn btn--ghost"
                              onClick={cancelEdit}
                              disabled={saving}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="btn btn--ghost" onClick={() => beginEdit(row)}>
                              Edit
                            </button>

                            <button
                              className="btn btn--ghost"
                              onClick={() => onDelete(row)}
                              disabled={deletingId === row.id}
                            >
                              {deletingId === row.id ? 'Deleting...' : 'Delete'}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>

                    {editing && (
                      <tr key={`${row.id}-locations`}>
                        <td colSpan={6} style={{ paddingTop: 0 }}>
                          <div className="load-template-edit">
                            <div className="load-template-edit__title">
                              Assigned Customer Locations
                            </div>

                            <div className="muted tiny load-template-edit__description">
                              Add or remove locations where this fulfilment method should be
                              available.
                            </div>

                            <CustomerLocationPicker
                              search={editLocationSearch}
                              onSearchChange={setEditLocationSearch}
                              locations={editLocations}
                              isLoading={editLocationsLoading}
                              selectedIds={editLocationIds}
                              onChange={toggleEditLocation}
                              hasMore={!!editLocationsHasMore}
                              isLoadingMore={editLocationsFetchingMore}
                              onLoadMore={() => fetchMoreEditLocations()}
                              variant="edit"
                            />

                            <div className="muted tiny load-template-edit__hint">
                              Removing this method from a location will automatically move its
                              default to another assigned method when possible.
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}

              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted ta-center">
                    No templates found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
